import { useState } from 'react'
import { supabase } from '@core/supabase'
import { Button, Card, ErrorNote, Field, Screen } from '@design/components'

/**
 * Sign in with a numeric code sent by email.
 *
 * Two reasons this is a code and NOT a magic link, both worth remembering
 * before anyone "improves" it:
 *
 * 1. A magic link opens in Safari, not in the installed home-screen app. The
 *    session would land in the browser while the app the family actually uses
 *    stays signed out — and on iOS those are separate storage contexts. The
 *    code keeps the whole flow inside the installed app.
 * 2. Supabase's passkey support is still Beta. Passkeys are the right
 *    long-term answer (Face ID, nothing to remember) and CLAUDE.md still names
 *    them as the target; this is the working path until they are stable.
 *
 * See docs/DECISIONS.md.
 */
/**
 * Supabase's OTP length is a dashboard setting (6–10 digits) that can be
 * changed without touching this code. Hardcoding 6 meant a project configured
 * for 8 silently locked everyone out: the input simply refused the last two
 * digits, with no error to explain it. Accept the whole supported range and
 * let the server decide whether the code is right.
 */
const MIN_CODE = 6
const MAX_CODE = 10

export function SignIn() {
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [stage, setStage] = useState<'email' | 'code'>('email')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function sendCode(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { shouldCreateUser: true },
    })
    setBusy(false)
    if (error) return setError(translate(error.message))
    setStage('code')
  }

  async function verify(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    const error = await verifyCode(email.trim(), code.trim())
    setBusy(false)
    if (error) setError(translate(error))
    // On success the auth listener in App swaps the screen; nothing to do here.
  }

  return (
    <Screen className="flex flex-col justify-center max-w-md mx-auto w-full pad-safe-top">
      <h1 className="text-2xl font-semibold mb-1">Familie</h1>
      <p className="text-muted mb-6">
        {stage === 'email'
          ? 'Vul je e-mailadres in. Je krijgt een code per e-mail.'
          : `We hebben een code gestuurd naar ${email}.`}
      </p>

      <Card>
        {stage === 'email' ? (
          <form onSubmit={sendCode}>
            <Field
              label="E-mailadres"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jij@voorbeeld.be"
            />
            <ErrorNote>{error}</ErrorNote>
            <Button type="submit" disabled={busy || !email.trim()}>
              {busy ? 'Bezig…' : 'Stuur mij een code'}
            </Button>
          </form>
        ) : (
          <form onSubmit={verify}>
            <Field
              label="Code uit de e-mail"
              type="text"
              inputMode="numeric"
              // Lets iOS offer the code straight from the notification banner,
              // so a parent never has to switch to Mail and back.
              autoComplete="one-time-code"
              maxLength={MAX_CODE}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="123456"
            />
            <ErrorNote>{error}</ErrorNote>
            <Button type="submit" disabled={busy || code.length < MIN_CODE}>
              {busy ? 'Bezig…' : 'Aanmelden'}
            </Button>
            <div className="mt-3">
              <Button
                tone="quiet"
                type="button"
                onClick={() => {
                  setStage('email')
                  setCode('')
                  setError('')
                }}
              >
                Ander e-mailadres gebruiken
              </Button>
            </div>
          </form>
        )}
      </Card>
    </Screen>
  )
}

/**
 * Verify the emailed code, whichever kind Supabase decided to send.
 *
 * A returning account gets an OTP token, verified as `email`. A brand-new
 * address, because "Confirm email" is on, gets a *signup* token instead —
 * same six digits in the same-looking email, but it must be verified as
 * `signup` or it is rejected as invalid.
 *
 * The client cannot reliably tell which case it is in: it does not know
 * whether the address already existed, and finding out would leak that fact
 * to anyone who asked. So try the common case, and fall back.
 *
 * Without this, the app works perfectly for whoever set it up and rejects
 * every parent's first ever sign-in with "that code is wrong" — the worst
 * possible first impression, and the hardest to debug, because the code IS
 * right.
 */
async function verifyCode(email: string, token: string): Promise<string | null> {
  const first = await supabase.auth.verifyOtp({ email, token, type: 'email' })
  if (!first.error) return null

  const second = await supabase.auth.verifyOtp({ email, token, type: 'signup' })
  if (!second.error) return null

  // Report the first failure: if both fail the code is genuinely wrong or
  // expired, and that message is the more accurate of the two.
  return first.error.message
}

/** Supabase errors are in English and unhelpful to a parent. */
function translate(message: string): string {
  const m = message.toLowerCase()
  if (m.includes('invalid') && m.includes('token')) return 'Die code klopt niet. Probeer opnieuw.'
  if (m.includes('expired')) return 'Die code is verlopen. Vraag een nieuwe aan.'
  if (m.includes('rate') || m.includes('too many')) return 'Even wachten, en dan opnieuw proberen.'
  if (m.includes('email')) return 'Dat e-mailadres lijkt niet te kloppen.'
  return 'Er ging iets mis. Probeer het opnieuw.'
}
